import React, { useCallback } from "react";
import cx from "classnames";
import type { StreamType } from "types/ContentTypes";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { useSignIn } from "@/features/auth/hooks/useSignIn";
import { useStreamStore } from "@/features/stream/stores/streamStore";
import { ReactComponent as LiveIcon } from "./icon-live.svg";
import { IconButton } from "@picklebet/ui";
import { differenceInMinutes } from "date-fns";

import * as styles from "./StreamButton.module.scss";

type StreamButtonProps<T> = T & {
  title: string;
  startTime?: Date;
  className?: string;
  eventUrl?: string;
  isPlaceholder?: boolean;
  isCTA?: boolean;
  /**
   * Hides the stream button if the event isn't within 30 minutes of starting
   * @default false
   */
  hideIfNotThirtyMinutesBeforeStart?: boolean;
};

const normaliseURL = (url: string) => {
  if (url && url.endsWith("/")) {
    return url.substring(0, url.length - 1);
  }
  return url;
};

const isAfterThirtyMinutes = (startTime?: Date) => {
  if (!(startTime instanceof Date)) return true;

  const diffInMins = differenceInMinutes(startTime, new Date());
  return diffInMins >= 30;
};

const StreamButton = ({
  url,
  className,
  isPlaceholder = false,
  hideIfNotThirtyMinutesBeforeStart = false,
  isCTA = false,
  ...props
}: StreamButtonProps<StreamType>) => {
  const { handleSignIn } = useSignIn();
  const doOpenStream = useStreamStore((state) => state.openStream);
  const isLoggedIn = useIsLoggedIn();

  const openStream = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();

      if (isLoggedIn) {
        doOpenStream({
          url: normaliseURL(url),
          eventId: props.eventId,
          eventUrl: props.eventUrl,
          sport: props.sport,
          title: props.title,
          muted: true,
          playing: true,
        });
      } else {
        handleSignIn();
      }
    },
    [isLoggedIn, props.eventId],
  );

  if (
    (!url && !isPlaceholder) ||
    (hideIfNotThirtyMinutesBeforeStart && isAfterThirtyMinutes(props.startTime))
  ) {
    //no stream or placeholder so ignore
    return null;
  }

  if (isCTA) {
    return (
      <button className={styles.ctaButton} onClick={openStream}>
        <LiveIcon />
        <span>Watch</span>
      </button>
    );
  }

  return (
    <IconButton
      className={cx(styles.streamButton, className)}
      variant="danger"
      size="xxs"
      onClick={openStream}
    >
      <LiveIcon />
    </IconButton>
  );
};

export { StreamButton };
export type { StreamButtonProps };
